import Service, { type Registry as Services, service } from '@ember/service';
import { isPlatformDomain } from 'frontend-utils/utils/domain-checks';
import ENV from 'lumen/config/environment';

declare global {
  interface Window {
    satismeter?: (options: {
      traits: {
        email?: string;
        name: string;
        products: string;
        signUpDate: string;
      };
      userId: string;
      writeKey: string;
    }) => void;
  }
}

export default class SatismeterService extends Service {
  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  isEnabledInEnvironment = ENV.satismeter.enabled;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private get availableProductsString() {
    const availableProducts = [];

    // https://otainsight.atlassian.net/browse/RATE-609
    if (this.access.hasOverviewDashboardTab) {
      availableProducts.push('overview');
    }
    if (this.access.hasMarketInsight) {
      availableProducts.push('market');
    }
    if (this.access.hasRates) {
      availableProducts.push('rate');
    }
    if (this.access.hasShortTermRentalAccess) {
      availableProducts.push('rate_plus');
    }
    if (this.access.hasParity || this.access.hasParityInsightAccess) {
      availableProducts.push('parity');
    }
    if (
      this.access.hasRevenueInsight ||
      this.access.hasSpider ||
      this.access.hasSpiderMarket
    ) {
      availableProducts.push('bi');
    }
    if (this.access.hasBenchmarkInsight) {
      availableProducts.push('benchmark');
    }
    if (this.access.hasPricingAssistantTab) {
      availableProducts.push('pricing_manager');
    }

    return availableProducts.join(',');
  }

  initialise(): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    // This code is copied from the satismeter install guide. Hence why we disabled eslint and type checking on it.
    try {
      /* eslint-disable */
      (function () {
        window.satismeter =
          window.satismeter ||
          function () {
            // @ts-expect-error
            (window.satismeter.q = window.satismeter.q || []).push(arguments);
          };
        // @ts-expect-error
        window.satismeter.l = 1 * new Date();
        const script = document.createElement('script');
        // @ts-expect-error
        const parent = document.getElementsByTagName('script')[0].parentNode;
        // @ts-expect-error
        script.async = 1;
        script.src = 'https://app.satismeter.com/satismeter.js';
        // @ts-expect-error
        parent.appendChild(script);
      })();
    } catch (e) {}
    /* eslint-enable */
  }

  sendTraits(): void {
    const { user } = this.hotelAndMarketService;

    let benchmarkInsightSubscriptionStatus: string =
      this.hotelAndMarketService.hotel?.productState?.benchmark_insight
        ?.subscription_status ?? '';

    if (this.hotelAndMarketService.hotel?.benchmarkInsightIsLight) {
      benchmarkInsightSubscriptionStatus = 'light';
    }

    if (window.satismeter) {
      try {
        const traits: {
          benchmarkInsightSubscriptionStatus: string;
          email?: string;
          name: string;
          products: string;
          signUpDate: string;
        } = {
          benchmarkInsightSubscriptionStatus,
          name: `${user.firstName} ${user.lastName}`,
          signUpDate: user.createdOn?.toISOString() ?? '',
          products: this.availableProductsString,
        };

        if (isPlatformDomain()) {
          traits.email = user?.email;
        }

        window.satismeter({
          writeKey: 'myG7CxmM9',
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string'.
          userId: user?.id,
          traits,
        });
      } catch (error) {
        this.errorTracking.captureException(error);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    satismeter: SatismeterService;
  }
}
